import { handlePaginatedData } from '../services/DataHelperFunctions';
import { EAction } from '../data/Constants';
import AxiosClient from './AxiosClient';
import { handleSuccess, handleError } from './HttpUtil';
import type { Dispatch } from '@reduxjs/toolkit';
import type {
  ILovOptionRequest,
  ILovOptionUpdateRequest,
  ILovOptionDeleteRequest,
} from '../../types/IApiTypes';

/**
 * API client for options belonging to a list of values (LOV)
 */
export const LovOptionsApi = {
  /**
   * Get the paginated options belonging to a LOV
   *
   * @param action - Request with the parent lovCode, page, limit and an optional search term
   * @param dispatch - Redux dispatch function
   * @returns Paginated LOV option data, or an error object on failure
   */
  getLovOption: (action: ILovOptionRequest, dispatch: Dispatch) => {
    const queryParams = new URLSearchParams({
      lovCode: action.lovCode,
      Page: String(action.page),
      Limit: String(action.limit),
    });
    if (action.search) queryParams.set('search', action.search);
    return AxiosClient.get(`/lov/v1/option/read?${queryParams}`)
      .then((response) =>
        handlePaginatedData(response, {
          page: action.page,
          limit: action.limit,
          searchCode: action.search,
        })
      )
      .catch((err) => handleError(dispatch, err, EAction.Read));
  },

  /**
   * Create or update an option within a LOV
   *
   * @param action - Option payload with parent lovCode, code, name and optional active flag
   * @param dispatch - Redux dispatch function
   * @returns Success response data, or an error object on failure
   */
  updateLovOption: (action: ILovOptionUpdateRequest, dispatch: Dispatch) => {
    return AxiosClient.post('/lov/v1/option/update', {
      lovCode: action.lovCode,
      code: action.code,
      name: action.name,
      isActive: action.isActive,
    })
      .then((response) => handleSuccess(response))
      .catch((err) => handleError(dispatch, err, EAction.Update));
  },

  /**
   * Delete one or more options from a LOV
   *
   * @param action - Object with the parent lovCode and the option codes to delete
   * @param dispatch - Redux dispatch function
   * @returns Success response data, or an error object on failure
   */
  deleteLovOption: (action: ILovOptionDeleteRequest, dispatch: Dispatch) => {
    return AxiosClient.post('/lov/v1/option/delete', {
      lovCode: action.lovCode,
      options: action.options,
    })
      .then((response) => handleSuccess(response))
      .catch((err) => handleError(dispatch, err));
  },
};
